<template>
  <section
    class="relative block w-full mx-0 px-0"
    :style="transitionStyle"
    @touchstart.passive="onTouchStart"
    @touchend="onTouchEnd"
  >
    <div
      class="relative w-full overflow-hidden bg-white aspect-[8/3] max-h-[720px]"
    >
      <div
        class="relative flex h-full w-full min-w-full items-center justify-center overflow-hidden bg-white"
      >
        <Transition :name="transitionName">
          <div v-if="activeSlide" :key="activeSlide.id" class="carousel-slide">
            <component
              :is="resolveSlideTag(activeSlide.link)"
              :to="
                isRelativeLink(activeSlide.link) ? activeSlide.link : undefined
              "
              :href="resolveSlideHref(activeSlide.link)"
              class="flex h-full w-full items-center justify-center overflow-hidden bg-white"
              :target="isExternalLink(activeSlide.link) ? '_blank' : undefined"
              :rel="isExternalLink(activeSlide.link) ? 'noopener' : undefined"
            >
              <img
                :src="activeSlide.image"
                :alt="activeSlide.title || 'Слайд'"
                class="block h-full w-full max-h-full max-w-full object-fill object-center transition-transform duration-700 ease-out"
                loading="lazy"
              />
            </component>
          </div>
        </Transition>
      </div>

      <div
        v-if="slides.length > 1"
        class="pointer-events-none absolute inset-x-0 bottom-4 z-20 flex items-end justify-between px-6 sm:bottom-12 sm:left-24 sm:right-auto sm:inset-x-auto sm:px-0"
      >
        <div
          class="flex w-full items-center justify-between sm:w-auto sm:justify-start sm:gap-4 md:gap-5"
        >
          <button
            class="pointer-events-auto flex h-11 w-11 items-center justify-center text-gray-dark transition active:text-orange sm:shadow-panel-soft sm:h-11 sm:w-11 sm:rounded-full sm:border sm:border-gray-border sm:bg-white-card hover:text-orange md:h-12 md:w-12"
            type="button"
            aria-label="Предыдущий"
            @click="() => prev()"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              class="h-6 w-6 sm:h-4 sm:w-4"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <path
                d="M15 18L9 12l6-6"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </button>
          <button
            class="pointer-events-auto flex h-11 w-11 items-center justify-center text-gray-dark transition active:text-orange sm:shadow-panel-soft sm:h-11 sm:w-11 sm:rounded-full sm:border sm:border-gray-border sm:bg-white-card hover:text-orange md:h-12 md:w-12"
            type="button"
            aria-label="Следующий"
            @click="() => next()"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              class="h-6 w-6 sm:h-4 sm:w-4"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <path
                d="m9 6 6 6-6 6"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </button>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { useHomeNews } from "~/composables/useHomeNews";

type CarouselSlide = {
  id: string | number;
  image: string;
  link?: string;
  title?: string;
};

const carouselConfig = useAppConfig().carousel ?? {
  autoSlideDelayMs: 12000,
  animationDurationMs: 500,
  leaveOpacity: 0.35,
  fadeEnabled: true,
};

const { banners, loadNews } = useHomeNews();

const currentIndex = ref(0);
const slides = computed<CarouselSlide[]>(() => {
  return banners.value;
});
const activeSlide = computed(() => slides.value[currentIndex.value]);
const slideDirection = ref<"next" | "prev">("next");
const transitionName = computed(() =>
  slideDirection.value === "next" ? "slide-left" : "slide-right",
);
const transitionStyle = computed(() => {
  const fadeEnabled = carouselConfig.fadeEnabled ?? true;
  const leaveOpacity = fadeEnabled ? (carouselConfig.leaveOpacity ?? 0.35) : 1;
  const enterOpacity = fadeEnabled ? 0 : 1;
  return {
    "--carousel-transition-duration": `${carouselConfig.animationDurationMs}ms`,
    "--carousel-leave-opacity": `${leaveOpacity}`,
    "--carousel-enter-opacity": `${enterOpacity}`,
  };
});
const startX = ref<number | null>(null);
const swipeThreshold = 50;
const isAnimating = ref(false);

let autoSlideTimer: NodeJS.Timeout | null = null;
const requestUrl = useRequestURL();

const normalizeHostname = (value: string) =>
  value
    .trim()
    .toLowerCase()
    .replace(/^www\./, "");

const currentHostname = computed(() => normalizeHostname(requestUrl.hostname));

const hasLink = (value?: string) => {
  return typeof value === "string" && value.trim().length > 0;
};

const isRelativeLink = (value?: string) =>
  typeof value === "string" && value.startsWith("/");

const isSameDomainLink = (value?: string) => {
  if (!hasLink(value) || isRelativeLink(value)) return false;

  try {
    const url = new URL(value!);
    if (!["http:", "https:"].includes(url.protocol)) return false;
    return normalizeHostname(url.hostname) === currentHostname.value;
  } catch {
    return false;
  }
};

const isExternalLink = (value?: string) => {
  if (!hasLink(value) || isRelativeLink(value)) return false;

  try {
    const url = new URL(value!);
    if (!["http:", "https:"].includes(url.protocol)) return false;
    return normalizeHostname(url.hostname) !== currentHostname.value;
  } catch {
    return false;
  }
};

const resolveSlideTag = (value?: string) => {
  if (!hasLink(value)) return "div";
  if (isRelativeLink(value)) return "NuxtLink";
  if (isSameDomainLink(value) || isExternalLink(value)) return "a";
  return "div";
};

const resolveSlideHref = (value?: string) => {
  if (!hasLink(value) || isRelativeLink(value)) return undefined;
  return value?.trim();
};

function scheduleAutoSlide() {
  if (autoSlideTimer) clearTimeout(autoSlideTimer);
  if (slides.value.length <= 1) return;
  autoSlideTimer = setTimeout(() => {
    next(false);
    scheduleAutoSlide();
  }, carouselConfig.autoSlideDelayMs);
}

function markAnimating() {
  isAnimating.value = true;
  setTimeout(
    () => (isAnimating.value = false),
    carouselConfig.animationDurationMs,
  );
}

function prev(isUserAction = true) {
  if (isAnimating.value) return;
  slideDirection.value = "prev";
  markAnimating();
  currentIndex.value =
    (currentIndex.value - 1 + slides.value.length) % slides.value.length;
  if (isUserAction) scheduleAutoSlide();
}

function next(isUserAction = true) {
  if (isAnimating.value) return;
  slideDirection.value = "next";
  markAnimating();
  currentIndex.value = (currentIndex.value + 1) % slides.value.length;
  if (isUserAction) scheduleAutoSlide();
}

function onTouchStart(event: TouchEvent) {
  startX.value = event.touches[0]?.clientX ?? null;
}

function onTouchEnd(event: TouchEvent) {
  if (startX.value === null) return;
  const endX = event.changedTouches[0]?.clientX ?? startX.value;
  const delta = endX - startX.value;

  if (Math.abs(delta) > swipeThreshold) {
    if (delta > 0) {
      prev();
    } else {
      next();
    }
  }

  startX.value = null;
}

onMounted(() => {
  void loadNews();
  scheduleAutoSlide();
});

watch(
  () => slides.value.length,
  (length) => {
    if (!length) {
      currentIndex.value = 0;
      return;
    }

    if (currentIndex.value >= length) {
      currentIndex.value = 0;
    }

    scheduleAutoSlide();
  },
);

onBeforeUnmount(() => {
  if (autoSlideTimer) clearTimeout(autoSlideTimer);
});
</script>

<style scoped>
.slide-left-enter-active,
.slide-left-leave-active,
.slide-right-enter-active,
.slide-right-leave-active {
  transition:
    transform var(--carousel-transition-duration, 500ms) ease,
    opacity var(--carousel-transition-duration, 500ms) ease;
  will-change: transform, opacity;
}

.slide-left-enter-active,
.slide-right-enter-active {
  z-index: 1;
}

.slide-left-leave-active,
.slide-right-leave-active {
  z-index: 2;
}

.slide-left-enter-from {
  opacity: var(--carousel-enter-opacity, 0);
  transform: translateX(100%);
}

.slide-left-leave-to {
  opacity: var(--carousel-leave-opacity, 0.35);
  transform: translateX(-100%);
}

.slide-right-enter-from {
  opacity: var(--carousel-enter-opacity, 0);
  transform: translateX(-100%);
}

.slide-right-leave-to {
  opacity: var(--carousel-leave-opacity, 0.35);
  transform: translateX(100%);
}
</style>
